import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { chapters } from '../data/chapters';
import { useLearning } from '../context/LearningContext';

function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return chapters.slice(0, 6);
    return chapters.filter(chapter => JSON.stringify(chapter).toLowerCase().includes(normalized)).slice(0, 8);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  if (!open) return null;
  return <div className="search-overlay" role="presentation" onMouseDown={onClose}>
    <div className="search-dialog" role="dialog" aria-modal="true" aria-label="全文搜索" onMouseDown={event => event.stopPropagation()}>
      <div className="search-field"><span>⌕</span><input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索 GPIO、PWM、HardFault…" aria-label="搜索课程" /><kbd>ESC</kbd></div>
      <div className="search-results"><span className="section-kicker">{query ? `${results.length} 个相关章节` : '快速前往'}</span>{results.length ? results.map(chapter => <button key={chapter.id} onClick={() => { navigate(`/chapter/${chapter.slug}`); onClose(); }}><i>{String(chapter.id).padStart(2, '0')}</i><span><strong>{chapter.shortTitle}</strong><small>{chapter.summary}</small></span><b>→</b></button>) : <div className="empty-search"><strong>没有找到相关章节</strong><span>试试“时钟”“串口”或“中断”。</span></div>}</div>
    </div>
  </div>;
}

export function Layout() {
  const { completed, theme, setTheme } = useLearning();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const current = chapters.find(chapter => location.pathname.endsWith(chapter.slug));
  const progress = Math.round(completed.length / chapters.length * 100);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', openSearch);
    return () => window.removeEventListener('keydown', openSearch);
  }, []);

  return <div className="app-shell">
    <a className="skip-link" href="#main-content">跳到主要内容</a>
    <aside className={`sidebar ${menuOpen ? 'open' : ''}`} aria-label="课程导航">
      <Link className="brand" to="/" aria-label="返回课程首页"><span className="chip-logo"><i /><i /><i /><i /></span><span><strong>STM32</strong><small>FROM ZERO TO SYSTEM</small></span></Link>
      <div className="sidebar-progress"><div><span>学习进度</span><strong>{progress}%</strong></div><div className="progress-track"><i style={{ width: `${progress}%` }} /></div><small>{completed.length} / {chapters.length} 章已完成</small></div>
      <nav>{chapters.map(chapter => <NavLink key={chapter.id} to={`/chapter/${chapter.slug}`} className={({ isActive }) => `${isActive ? 'active' : ''} ${completed.includes(chapter.id) ? 'completed' : ''}`}><i>{completed.includes(chapter.id) ? '✓' : String(chapter.id).padStart(2, '0')}</i><span><strong>{chapter.shortTitle}</strong><small>{chapter.eyebrow}</small></span></NavLink>)}</nav>
      <div className="sidebar-note"><span>教学平台</span><strong>STM32F103C8T6</strong><small>CubeIDE · HAL · ST-Link · 3.3 V</small></div>
    </aside>
    {menuOpen && <button className="menu-scrim" onClick={() => setMenuOpen(false)} aria-label="关闭课程菜单" />}
    <div className="page-column">
      <header className="topbar">
        <button className="icon-button menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="打开课程菜单">☰</button>
        <div className="breadcrumbs"><Link to="/">学习路线</Link><span>/</span><strong>{current ? `第${current.id}章 · ${current.shortTitle}` : '总览'}</strong></div>
        <div className="top-actions"><button className="search-trigger" onClick={() => setSearchOpen(true)}><span>⌕</span><em>全文搜索</em><kbd>⌘ K</kbd></button><button className="icon-button" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label={`切换为${theme === 'dark' ? '浅色' : '深色'}主题`}>{theme === 'dark' ? '☀' : '☾'}</button></div>
      </header>
      <main id="main-content"><Outlet /></main>
      <footer><span>STM32 从入门到进阶</span><span>所有进度仅保存在当前浏览器 · 无登录 · 无数据收集</span></footer>
    </div>
    <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
  </div>;
}
